// Cycle Planner v3 - pill presets across both modes, dose-calc URL pre-fill preserved.

const LIMITS = {
  'dur-start':     { min: 0.001, max: 1000, label: 'mg' },
  'dur-increment': { min: 0,     max: 100,  label: 'mg', allowZero: true },
  'dur-weeks':     { min: 1,     max: 52,   label: 'weeks' },
  'dur-vial':      { min: 0.1,   max: 5000, label: 'mg' },
  'sup-vials':     { min: 1,     max: 100,  label: 'vials' },
  'sup-strength':  { min: 0.1,   max: 5000, label: 'mg' },
  'sup-dose':      { min: 0.001, max: 200,  label: 'mg' },
};

function validateField(field, value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { valid: false, error: 'Enter a number' };
  }
  const limit = LIMITS[field];
  if (!limit) return { valid: true };
  if (!limit.allowZero && value <= 0) return { valid: false, error: 'Must be greater than 0' };
  if (limit.allowZero && value < 0) return { valid: false, error: 'Cannot be negative' };
  if (value < limit.min || value > limit.max) {
    return { valid: false, error: `Must be between ${limit.min} and ${limit.max} ${limit.label}` };
  }
  return { valid: true };
}

export const STORAGE_KEY = 'is_cycle_planner_last';
export const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

export function saveLastPlan(snapshot, storage, nowMs) {
  storage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({}, snapshot, { timestampMs: nowMs })));
}

export function loadLastPlan(storage, nowMs) {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const d = JSON.parse(raw);
    if (typeof d.timestampMs !== 'number') return null;
    if (nowMs - d.timestampMs > EXPIRY_MS) return null;
    if (d.mode !== 'duration' && d.mode !== 'supply') return null;
    if (d.mode === 'duration') {
      if (!isFinite(d.durStart) || d.durStart <= 0) return null;
      if (!isFinite(d.durVial) || d.durVial <= 0) return null;
      if (!isFinite(d.durWeeks) || d.durWeeks <= 0) return null;
    } else {
      if (!isFinite(d.supVials) || d.supVials <= 0) return null;
      if (!isFinite(d.supStrength) || d.supStrength <= 0) return null;
      if (!isFinite(d.supDose) || d.supDose <= 0) return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function clearLastPlan(storage) {
  storage.removeItem(STORAGE_KEY);
}

export function buildSchedule({ startMg, incrementMg, cadenceWeeks, totalWeeks, frequencyPerWeek }) {
  const rows = [];
  let totalMg = 0;
  for (let week = 1; week <= totalWeeks; week++) {
    const stepIndex = Math.floor((week - 1) / cadenceWeeks);
    const dosePerInj = startMg + (incrementMg * stepIndex);
    const weeklyMg = dosePerInj * frequencyPerWeek;
    totalMg += weeklyMg;
    rows.push({ week, dosePerInj, injections: frequencyPerWeek, weeklyMg });
  }
  const grouped = [];
  for (const row of rows) {
    const last = grouped[grouped.length - 1];
    if (last && Math.abs(last.dosePerInj - row.dosePerInj) < 1e-9) {
      last.weekEnd = row.week;
      last.weeklyMgTotal = (last.weeklyMgTotal || last.weeklyMg) + row.weeklyMg;
    } else {
      grouped.push({ weekStart: row.week, weekEnd: row.week, dosePerInj: row.dosePerInj, injections: row.injections, weeklyMg: row.weeklyMg });
    }
  }
  return { groupedRows: grouped, totalMg, finalDose: rows[rows.length - 1].dosePerInj };
}

export function planBySupply({ vialCount, vialStrengthMg, doseMg, frequencyPerWeek }) {
  if (!isFinite(vialCount) || vialCount <= 0) return null;
  if (!isFinite(vialStrengthMg) || vialStrengthMg <= 0) return null;
  if (!isFinite(doseMg) || doseMg <= 0) return null;
  if (!isFinite(frequencyPerWeek) || frequencyPerWeek <= 0) return null;
  const totalMg = vialCount * vialStrengthMg;
  const totalInjections = Math.floor(totalMg / doseMg);
  const weeks = totalInjections / frequencyPerWeek;
  return { totalMg, totalInjections, weeks };
}

function formatMg(v) {
  if (v >= 10) return v.toFixed(1) + ' mg';
  if (v >= 1) return v.toFixed(2) + ' mg';
  return (v * 1000).toFixed(0) + ' mcg';
}
function formatWeekRange(start, end) {
  return start === end ? 'Week ' + start : 'Weeks ' + start + '-' + end;
}
function formatWeeks(w) {
  if (w >= 10) return w.toFixed(0);
  return (Math.round(w * 10) / 10).toString();
}
function formatDate(d) {
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

function boot() {
  const root = document.querySelector('.is-cycle');
  if (!root) return;
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => root.querySelectorAll(s);
  const form = $('#is-cycle-form');
  const empty = $('#is-cycle-result-empty');
  const durResult = $('#is-cycle-result-duration');
  const supResult = $('#is-cycle-result-supply');
  const totalEl = $('#is-cycle-total-mg');
  const vialsEl = $('#is-cycle-vials');
  const finalEl = $('#is-cycle-final-dose');
  const tbody = $('#is-cycle-schedule-body');
  const supWeeksEl = $('#is-cycle-supply-weeks');
  const supSubEl = $('#is-cycle-supply-subline');
  const supTotalEl = $('#is-cycle-supply-total-mg');
  const supInjEl = $('#is-cycle-supply-injections');
  const supRunoutEl = $('#is-cycle-supply-runout');
  const startDateEl = $('#is-cycle-supply-start');
  const doseCalcLink = $('#is-cycle-dose-calc-link');
  const modeButtons = $$('[data-mode-btn]');
  const cadencePills = $$('[data-cadence]');
  const freqDurPills = $$('[data-freq][data-mode-target="duration"]');
  const freqSupPills = $$('[data-freq][data-mode-target="supply"]');

  let mode = 'duration';
  const state = {
    'dur-start': null,
    'dur-increment': 0,
    'dur-weeks': 16,
    'dur-vial': null,
    'sup-vials': null,
    'sup-strength': null,
    'sup-dose': null,
    cadence: 4,
    durFreq: 1,
    supFreq: 1,
  };

  function hideResult() {
    empty.hidden = false;
    durResult.hidden = true;
    supResult.hidden = true;
  }

  function recomputeDuration() {
    const startMg = state['dur-start'];
    const incrementMg = state['dur-increment'];
    const totalWeeks = state['dur-weeks'];
    const vialMg = state['dur-vial'];
    if (!isFinite(startMg) || startMg <= 0 || !isFinite(incrementMg) || incrementMg < 0 ||
        !isFinite(totalWeeks) || totalWeeks <= 0) {
      hideResult();
      return;
    }
    const { groupedRows, totalMg, finalDose } = buildSchedule({
      startMg, incrementMg, cadenceWeeks: state.cadence, totalWeeks, frequencyPerWeek: state.durFreq,
    });
    totalEl.textContent = formatMg(totalMg);
    finalEl.textContent = formatMg(finalDose);
    if (isFinite(vialMg) && vialMg > 0) {
      const vials = Math.ceil(totalMg / vialMg);
      vialsEl.textContent = vials + ' x ' + vialMg + 'mg';
    } else {
      vialsEl.textContent = 'Pick vial strength';
    }
    tbody.innerHTML = groupedRows.map(r => {
      const span = (r.weekEnd - r.weekStart + 1);
      const weeklyMg = r.dosePerInj * r.injections;
      return '<tr>'
        + '<td>' + formatWeekRange(r.weekStart, r.weekEnd) + '</td>'
        + '<td>' + formatMg(r.dosePerInj) + '</td>'
        + '<td>' + r.injections + ' / week</td>'
        + '<td>' + formatMg(weeklyMg) + ' x ' + span + ' = ' + formatMg(weeklyMg * span) + '</td>'
        + '</tr>';
    }).join('');
    empty.hidden = true;
    durResult.hidden = false;
    supResult.hidden = true;
  }

  function recomputeSupply() {
    const result = planBySupply({
      vialCount: state['sup-vials'],
      vialStrengthMg: state['sup-strength'],
      doseMg: state['sup-dose'],
      frequencyPerWeek: state.supFreq,
    });
    if (!result) {
      hideResult();
      return;
    }
    supWeeksEl.textContent = formatWeeks(result.weeks);
    const vc = state['sup-vials'];
    supSubEl.textContent = vc + ' vial' + (vc === 1 ? '' : 's') + ' x ' + state['sup-strength'] + ' mg = ' + formatMg(result.totalMg) + ' total. At ' + formatMg(state['sup-dose']) + '/injection x ' + state.supFreq + '/week.';
    supTotalEl.textContent = formatMg(result.totalMg);
    supInjEl.textContent = result.totalInjections + ' total';
    const startRaw = startDateEl ? startDateEl.value : '';
    if (startRaw) {
      const start = new Date(startRaw + 'T00:00:00');
      if (!isNaN(start.getTime())) {
        const runout = new Date(start.getTime() + result.weeks * 7 * 24 * 3600 * 1000);
        supRunoutEl.textContent = formatDate(runout);
      } else {
        supRunoutEl.textContent = '-';
      }
    } else {
      supRunoutEl.textContent = 'Add start date';
    }
    empty.hidden = true;
    supResult.hidden = false;
    durResult.hidden = true;
  }

  const restoreChip = $('#is-cycle-restore-chip');
  const restoreSummaryEl = $('#is-cycle-restore-summary');
  const restoreYesBtn = $('#is-cycle-restore-yes');
  const restoreNoBtn = $('#is-cycle-restore-no');

  function hideRestoreChip() {
    if (restoreChip) { restoreChip.hidden = true; restoreChip.style.display = 'none'; }
  }

  function snapshotState() {
    return {
      mode,
      cadence: state.cadence,
      durStart: state['dur-start'],
      durIncrement: state['dur-increment'],
      durWeeks: state['dur-weeks'],
      durVial: state['dur-vial'],
      durFreq: state.durFreq,
      supVials: state['sup-vials'],
      supStrength: state['sup-strength'],
      supDose: state['sup-dose'],
      supFreq: state.supFreq,
    };
  }

  let savePlanTimer = null;
  function maybeSavePlan() {
    // Debounce localStorage writes: recompute fires on every keystroke,
    // but a synchronous setItem on every keystroke is wasteful.
    if (savePlanTimer) clearTimeout(savePlanTimer);
    savePlanTimer = setTimeout(() => {
      savePlanTimer = null;
      if (mode === 'duration') {
        const ok = isFinite(state['dur-start']) && state['dur-start'] > 0
                && isFinite(state['dur-vial']) && state['dur-vial'] > 0
                && isFinite(state['dur-weeks']) && state['dur-weeks'] > 0;
        if (!ok) return;
      } else {
        const ok = isFinite(state['sup-vials']) && state['sup-vials'] > 0
                && isFinite(state['sup-strength']) && state['sup-strength'] > 0
                && isFinite(state['sup-dose']) && state['sup-dose'] > 0;
        if (!ok) return;
      }
      saveLastPlan(snapshotState(), window.localStorage, Date.now());
    }, 250);
  }

  function updateDoseCalcLink() {
    if (!doseCalcLink) return;
    let vial = null;
    let dose = null;
    if (mode === 'duration') {
      vial = state['dur-vial'];
      dose = state['dur-start'];
    } else {
      vial = state['sup-strength'];
      dose = state['sup-dose'];
    }
    const params = new URLSearchParams();
    if (isFinite(vial) && vial > 0) params.set('vial', vial);
    if (isFinite(dose) && dose > 0) {
      // Cycle planner stores doses in mg; pass through as mg unit so dose-calc
      // does not silently reinterpret a 0.25 mg pill as 0.25 mcg.
      params.set('dose', dose);
      params.set('doseUnit', 'mg');
    }
    const qs = params.toString();
    doseCalcLink.href = '/pages/dose-volume-calculator' + (qs ? '?' + qs : '');
  }

  let userInteracted = false;

  // Mirror the current plan into the query string so the address bar is a
  // permalink to this result (IS #359). The read side below already existed;
  // without this, prefill links could only ever be hand-crafted.
  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;
    const p = new URLSearchParams();
    const num = (v) => isFinite(v) && v > 0;
    if (mode === 'supply') {
      p.set('mode', 'supply');
      if (num(state['sup-vials'])) p.set('supvials', state['sup-vials']);
      if (num(state['sup-strength'])) p.set('supstrength', state['sup-strength']);
      if (num(state['sup-dose'])) p.set('supdose', state['sup-dose']);
      if (num(state.supFreq) && state.supFreq !== 1) p.set('supfreq', state.supFreq);
    } else {
      if (num(state['dur-start'])) p.set('start', state['dur-start']);
      if (num(state['dur-increment'])) p.set('increment', state['dur-increment']);
      if (num(state['dur-weeks'])) p.set('weeks', state['dur-weeks']);
      if (num(state['dur-vial'])) p.set('vial', state['dur-vial']);
      if (num(state.durFreq) && state.durFreq !== 1) p.set('freq', state.durFreq);
    }
    if (num(state.cadence) && state.cadence !== 4) p.set('cadence', state.cadence);
    const qs = p.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  function recompute() {
    if (mode === 'duration') recomputeDuration();
    else recomputeSupply();
    updateDoseCalcLink();
    writeUrl();
    maybeSavePlan();
    if (userInteracted && restoreChip && !restoreChip.hidden) hideRestoreChip();
  }

  function markUserInteraction() { userInteracted = true; }

  function setMode(next) {
    if (next !== 'duration' && next !== 'supply') return;
    mode = next;
    form.dataset.activeMode = mode;
    modeButtons.forEach(b => {
      const active = b.dataset.modeBtn === mode;
      b.classList.toggle('is-cycle__mode-btn--active', active);
      b.setAttribute('aria-checked', active ? 'true' : 'false');
    });
    recompute();
  }

  // Pill groups with Other fallback. Returns a function to programmatically pick a value (used for URL pre-fill).
  function bindPillGroup(field) {
    const pills = $$(`.is-calc__pill[data-field="${field}"]`);
    const otherBlock = root.querySelector(`.is-calc__other-input[data-field="${field}"]`);
    const otherInput = otherBlock ? otherBlock.querySelector('input') : null;

    function activate(pill, raw) {
      pills.forEach(p => {
        p.classList.remove('is-calc__pill--active');
        p.setAttribute('aria-checked', 'false');
      });
      pill.classList.add('is-calc__pill--active');
      pill.setAttribute('aria-checked', 'true');
      if (pill.dataset.value === 'other') {
        if (otherBlock) otherBlock.hidden = false;
        if (otherInput) {
          if (raw !== undefined) otherInput.value = String(raw);
          const v = parseFloat(otherInput.value);
          state[field] = isFinite(v) ? v : null;
        } else {
          state[field] = null;
        }
      } else {
        if (otherBlock) {
          otherBlock.hidden = true;
          if (otherInput && raw === undefined) otherInput.value = '';
        }
        state[field] = parseFloat(pill.dataset.value);
      }
    }

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        markUserInteraction();
        activate(pill);
        if (pill.dataset.value === 'other' && otherInput) otherInput.focus();
        recompute();
      });
    });
    const errEl = otherBlock ? otherBlock.querySelector('.is-calc__error') : null;
    if (otherInput) {
      otherInput.addEventListener('input', () => {
        markUserInteraction();
        const raw = otherInput.value.trim();
        if (!raw) {
          state[field] = null;
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
          recompute();
          return;
        }
        const v = parseFloat(raw);
        const check = validateField(field, v);
        if (check.valid) {
          state[field] = v;
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
        } else {
          state[field] = null;
          if (errEl) { errEl.textContent = check.error; errEl.hidden = false; }
        }
        recompute();
      });
    }

    // Caller helper: programmatic value -> matching pill or Other-with-value
    return function selectByValue(value) {
      if (value === null || value === undefined || value === '') return;
      const num = parseFloat(value);
      if (!isFinite(num)) return;
      let match = null;
      pills.forEach(p => {
        if (p.dataset.value !== 'other' && Math.abs(parseFloat(p.dataset.value) - num) < 1e-9) match = p;
      });
      if (match) {
        activate(match);
      } else {
        const otherPill = root.querySelector(`.is-calc__pill--other[data-field="${field}"]`);
        if (otherPill) activate(otherPill, num);
      }
    };
  }

  // Wire all pill groups
  const selectStart = bindPillGroup('dur-start');
  const selectIncrement = bindPillGroup('dur-increment');
  const selectWeeks = bindPillGroup('dur-weeks');
  const selectVial = bindPillGroup('dur-vial');
  const selectSupVials = bindPillGroup('sup-vials');
  const selectSupStrength = bindPillGroup('sup-strength');
  const selectSupDose = bindPillGroup('sup-dose');

  // Pre-activate the default-marked pills (16 weeks, no escalation pill not pre-active so set 0 increment from state default)
  const preActiveWeeks = root.querySelector('.is-calc__pill[data-field="dur-weeks"].is-calc__pill--active');
  if (preActiveWeeks) state['dur-weeks'] = parseFloat(preActiveWeeks.dataset.value);

  // Cadence pills (no Other - 4 fixed values)
  function bindSimplePillGroup(pills, onPick) {
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        markUserInteraction();
        const v = parseFloat(pill.dataset.cadence || pill.dataset.freq);
        if (!isFinite(v) || v <= 0) return;
        pills.forEach(p => {
          const active = p === pill;
          p.classList.toggle('is-calc__pill--active', active);
          p.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        onPick(v);
        recompute();
      });
    });
  }
  bindSimplePillGroup(cadencePills, v => { state.cadence = v; });
  bindSimplePillGroup(freqDurPills, v => { state.durFreq = v; });
  bindSimplePillGroup(freqSupPills, v => { state.supFreq = v; });

  function selectSimpleByValue(pills, value, onPick) {
    const num = parseFloat(value);
    if (!isFinite(num) || num <= 0) return false;
    let matched = null;
    pills.forEach(p => {
      const v = parseFloat(p.dataset.cadence || p.dataset.freq);
      const active = Math.abs(v - num) < 1e-9;
      p.classList.toggle('is-calc__pill--active', active);
      p.setAttribute('aria-checked', active ? 'true' : 'false');
      if (active) matched = p;
    });
    if (matched) { onPick(num); return true; }
    return false;
  }

  // Mode toggle
  modeButtons.forEach(b => b.addEventListener('click', () => { markUserInteraction(); setMode(b.dataset.modeBtn); }));

  // Date picker for Supply runout
  if (startDateEl) {
    startDateEl.addEventListener('input', () => { markUserInteraction(); recompute(); });
    startDateEl.addEventListener('change', () => { markUserInteraction(); recompute(); });
  }

  // URL pre-fill (linked from dose calc's 'Plan a cycle' button)
  const params = new URLSearchParams(window.location.search);
  const hasUrlPrefill = params.has('start') || params.has('vial') || params.has('mode') ||
                        params.has('weeks') || params.has('cadence') || params.has('freq') ||
                        params.has('increment') || params.has('supvials') ||
                        params.has('supstrength') || params.has('supdose') || params.has('supfreq');
  if (params.get('mode') === 'supply') setMode('supply');
  if (params.has('start')) selectStart(params.get('start'));
  if (params.has('increment')) selectIncrement(params.get('increment'));
  if (params.has('weeks')) selectWeeks(params.get('weeks'));
  if (params.has('vial')) selectVial(params.get('vial'));
  if (params.has('supvials')) selectSupVials(params.get('supvials'));
  if (params.has('supstrength')) selectSupStrength(params.get('supstrength'));
  if (params.has('supdose')) selectSupDose(params.get('supdose'));
  if (params.has('cadence')) selectSimpleByValue(cadencePills, params.get('cadence'), v => { state.cadence = v; });
  if (params.has('freq')) selectSimpleByValue(freqDurPills, params.get('freq'), v => { state.durFreq = v; });
  if (params.has('supfreq')) selectSimpleByValue(freqSupPills, params.get('supfreq'), v => { state.supFreq = v; });

  // Restore-banner wiring. The banner is shown by the inline pre-paint script
  // in cycle-planner.liquid; here we apply or clear it on user choice.
  function applyRestoredPlan(d) {
    setMode(d.mode);
    if (d.mode === 'duration') {
      if (isFinite(d.durStart)) selectStart(d.durStart);
      if (isFinite(d.durIncrement)) selectIncrement(d.durIncrement);
      if (isFinite(d.durWeeks)) selectWeeks(d.durWeeks);
      if (isFinite(d.durVial)) selectVial(d.durVial);
      if (isFinite(d.cadence)) selectSimpleByValue(cadencePills, d.cadence, v => { state.cadence = v; });
      if (isFinite(d.durFreq)) selectSimpleByValue(freqDurPills, d.durFreq, v => { state.durFreq = v; });
    } else {
      if (isFinite(d.supVials)) selectSupVials(d.supVials);
      if (isFinite(d.supStrength)) selectSupStrength(d.supStrength);
      if (isFinite(d.supDose)) selectSupDose(d.supDose);
      if (isFinite(d.supFreq)) selectSimpleByValue(freqSupPills, d.supFreq, v => { state.supFreq = v; });
    }
    recompute();
  }

  if (restoreYesBtn) {
    restoreYesBtn.addEventListener('click', () => {
      const d = loadLastPlan(window.localStorage, Date.now());
      hideRestoreChip();
      if (d) {
        userInteracted = true;
        applyRestoredPlan(d);
      }
    });
  }
  if (restoreNoBtn) {
    restoreNoBtn.addEventListener('click', () => {
      clearLastPlan(window.localStorage);
      hideRestoreChip();
    });
  }

  recompute();
  // If URL pre-fill brought any fields, mark interaction so first user click
  // doesn't unhide a stale banner.
  if (hasUrlPrefill) { userInteracted = true; hideRestoreChip(); }
}
// build 1777524650
